import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { BulkIntakeService } from "@/services/bulkIntakeService";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { CheckCircle, XCircle } from "lucide-react";
import { classifyConfirmationOutcome } from "@/utils/confirmationOutcome";
import { useCountdown } from "@/hooks/useCountdown";

/**
 * The page a bulk-intake confirmation email's "send me a new one" link
 * points to. POSTs to the resend endpoint on mount — see ResendConfirmation
 * (candidate flow) for the identical shape.
 *
 * "Already confirmed" is a distinct outcome from "sent" here: a resend that
 * comes back ALREADY_CONFIRMED sent nothing, so telling the recruiter a new
 * link is on its way would be wrong.
 */
const BulkIntakeConfirmationResend = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [outcome, setOutcome] = useState("error"); // "sent" | "already-confirmed" | "error"
  const [result, setResult] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [message, setMessage] = useState("");
  const { secondsLeft, start: startCountdown } = useCountdown();

  const requestResend = useCallback(async () => {
    if (!token) {
      setOutcome("error");
      setErrorCode(null);
      setMessage("Invalid confirmation link.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await BulkIntakeService.resendBatchConfirmation(token);
      setResult(response);
      setErrorCode(null);
      setOutcome("sent");
      setMessage(response?.message || "A new confirmation link is on its way. Please check your inbox.");
    } catch (error) {
      const code = error?.response?.data?.error_code;
      const classified = classifyConfirmationOutcome(code);
      setErrorCode(code);
      setResult(null);

      if (classified.status === "success") {
        setOutcome("already-confirmed");
        setMessage(error?.response?.data?.message || "This batch has already been confirmed.");
      } else {
        setOutcome("error");
        setMessage(error?.response?.data?.message || "Couldn't send a new link.");
        if (code === "CONFIRMATION_RESEND_TOO_SOON") {
          startCountdown(error?.response?.data?.data?.retry_after_seconds || 60);
        }
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    requestResend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canRetryAfterCooldown = outcome === "error" && classifyConfirmationOutcome(errorCode).canResend === "cooldown";

  return (
    <div className="min-h-[75vh] flex items-center justify-center px-4 sm:px-6 bg-background-primary">
      <Card className="max-w-md w-full p-8 text-center">
        {loading ? (
          <>
            <div className="loader text-text-primary mx-auto mb-4" />
            <h2 className="text-lg text-text-primary font-semibold">Sending a new confirmation link…</h2>
          </>
        ) : outcome === "sent" ? (
          <>
            <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
            <h2 className="text-xl text-text-primary font-semibold mb-2">New Link Sent</h2>
            <p className="text-text-secondary">{message}</p>

            {result && (
              <div className="mt-4 space-y-1 text-xs text-text-secondary">
                {result.email_masked && <p>Sent to {result.email_masked}</p>}
                {typeof result.expires_in_minutes === "number" && <p>This link expires in {result.expires_in_minutes} minutes.</p>}
                {typeof result.sends_remaining === "number" && (
                  <p>
                    {result.sends_remaining} resend{result.sends_remaining === 1 ? "" : "s"} left.
                  </p>
                )}
              </div>
            )}

            <Button onClick={() => navigate("/admin/jobs")} className="mt-5 text-xs bg-text-blue text-text-base rounded-lg px-4 py-2">
              Back to homepage
            </Button>
          </>
        ) : outcome === "already-confirmed" ? (
          <>
            <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
            <h2 className="text-xl text-text-primary font-semibold mb-2">Already Confirmed</h2>
            <p className="text-text-secondary">{message}</p>

            <Button onClick={() => navigate("/admin/jobs")} className="mt-5 text-xs bg-text-blue text-text-base rounded-lg px-4 py-2">
              Back to homepage
            </Button>
          </>
        ) : (
          <>
            <XCircle size={64} className="mx-auto text-status-error mb-4" />
            <h2 className="text-xl text-text-primary font-semibold mb-2">Couldn't Send a New Link</h2>
            <p className="text-text-secondary">{message}</p>

            {canRetryAfterCooldown && (
              <Button onClick={requestResend} disabled={secondsLeft > 0} className="mt-4 bg-button-secondary text-text-base rounded-lg px-4 py-2 text-xs">
                {secondsLeft > 0 ? `Try again in ${secondsLeft}s` : "Try again"}
              </Button>
            )}

            <Button
              onClick={() => navigate("/admin/jobs")}
              className="mt-5 text-xs bg-background-tertiary text-text-primary rounded-lg px-4 py-2 border border-border-primary"
            >
              Back to homepage
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};

export default BulkIntakeConfirmationResend;
